import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  HostListener,
  OnInit,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { AuthOverlayRef } from '../models/auth-overlay-ref.model';
import { AUTH_OVERLAY_DATA } from '../models/auth-overlay-config.interface';
import { Observable } from 'rxjs';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizes, ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import {
  AuthOverlayParamData,
  AuthOverlayModes,
  AuthOverlayRedirectCallback,
} from '../models/auth-overlay-data.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AuthIntroComponent } from '../../../auth/components/auth-intro/auth-intro.component';
import { AsyncPipe } from '@angular/common';
import { SmsLoginComponent } from '../../../auth/components/sms-sign-in/sms-sign-in.component';
import { EmailSignInComponent } from '../../../auth/components/email-sign-in/email-sign-in.component';

@Component({
  selector: 'app-auth-overlay',
  templateUrl: './auth-overlay.template.html',
  styleUrls: ['./auth-overlay.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuthIntroComponent, AsyncPipe, SmsLoginComponent, EmailSignInComponent],
})
export class AuthOverlayComponent implements OnInit {
  public silent?: boolean | null;
  public modeAtStart: string | null = null;
  public mode: AuthOverlayModes | null = null;
  public successRedirect: Array<string> | null = null;
  public successRedirectQueryParams: Params | null = null;
  public handleRedirectCallback: AuthOverlayRedirectCallback | null = null;
  public rtlMode: boolean;
  public isBrowser: boolean;
  public params: Params = {};
  public queryParams: Params = {};
  public routeData: Params = {};

  public screenSize$: Observable<ScreenSizes>;
  public lessThan$: Observable<ScreenSizeInfoState>;

  public email: string | null = null;
  public username: string | null = null;
  public previousMode: string | null = null;
  public nextMode: string | null = null;

  constructor(
    private readonly zone: NgZone,
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly authOverlayRef: AuthOverlayRef,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    @Inject(AUTH_OVERLAY_DATA) private readonly data: Partial<AuthOverlayParamData>
  ) {
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.mode = this.data.mode || 'auth-intro';
    this.successRedirect = this.data.successRedirect || null;
    this.successRedirectQueryParams = this.data.successRedirectQueryParams || {};
    this.handleRedirectCallback = this.data.handleRedirectCallback || null;
    this.modeAtStart = this.mode;
    this.silent = this.data.silent;
    this.email = this.data.email || null;
    this.rtlMode = false;

    if (this.mode === 'sign-up') {
      this.mode = 'auth-intro';
      this.nextMode = 'sign-up';
      this.modeAtStart = 'auth-intro';
    } else if (this.mode === 'force-sign-up') {
      this.mode = 'sign-up';
      this.nextMode = 'auth-intro';
      this.modeAtStart = 'sign-up';
    }

    this.screenSize$ = this.akitaScreenQuery.selectSize();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
  }

  public ngOnInit(): void {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }

    this.params = route.params || {};
    this.queryParams = route.queryParams || {};
    this.routeData = route.data || {};
    this.rtlMode = this.routeData.rtl || false;
  }

  @HostListener('document:keydown.escape', [])
  public handleKeydown(): void {
    this.closeOverlay();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public updateDialogMode($event: any | null) {
    if ($event) {
      this.email = $event.email || null;
      this.username = $event.username || null;
      this.previousMode = $event.previousMode || null;
      this.mode = $event.mode || 'auth-intro';
      this.changeDetector.markForCheck();
    }
  }

  public onHandleRedirect(): void {
    if (this.handleRedirectCallback) {
      this.handleRedirectCallback();
    }
  }

  public get skipRedirect(): boolean {
    return Boolean(this.handleRedirectCallback);
  }

  public closeOverlay(credentials?: string | boolean | null): void {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.authOverlayRef.dismissed = Boolean(credentials === true);

        if (credentials && credentials !== true) {
          this.authOverlayRef.credentials = `${credentials}`;
        } else {
          this.authOverlayRef.credentials = null;
        }

        this.authOverlayRef.close();
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      }, 500);
    });
  }
}
